import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../../utils/api';
import { Layout } from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, FileText, Download, Link as LinkIcon, Fire, Pause } from '@phosphor-icons/react';
import { GeneratePaymentLinkDialog } from '../../components/admin/GeneratePaymentLinkDialog';
import { useAuth } from '../../context/AuthContext';
import { formatAnyDate } from '../../utils/dateFormat';

const fmtINR = (n) => '₹ ' + Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });

function MobileJobOrderDetail() {
  const { jo_number } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isSales = user?.role === 'sales';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPL, setShowPL] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/api/admin/mobile-job-orders/${encodeURIComponent(jo_number)}`);
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [jo_number]);

  useEffect(() => { load(); }, [load]);

  if (loading || !data) {
    return <Layout><div className="text-center py-12">Loading...</div></Layout>;
  }

  const items = data.items || [];
  const overlay = data.overlay || {};

  return (
    <Layout>
      <div className="space-y-6 pb-12">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Button variant="ghost" data-testid="back-btn" onClick={() => navigate('/admin/mobile-job-orders')}>
              <ArrowLeft size={18} />
            </Button>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-light tracking-tight text-zinc-950 font-mono">{data.jo_number}</h1>
                {overlay.is_urgent && <Badge className="bg-red-100 text-red-700 hover:bg-red-100"><Fire size={12} className="mr-1" />URGENT</Badge>}
                {overlay.on_hold && <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100"><Pause size={12} className="mr-1" />HOLD</Badge>}
                {data._archived && <Badge variant="outline">ARCHIVED</Badge>}
              </div>
              <p className="text-sm text-zinc-500 mt-0.5">{data.customer_name} · {data.customer_mobile}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              data-testid="pdf-print-btn"
              variant="outline"
              onClick={() => window.open(`/api/admin/mobile-job-orders/${encodeURIComponent(jo_number)}/pdf`, '_blank')}
            >
              <FileText size={16} className="mr-1" /> Print PDF
            </Button>
            <a href={`/api/admin/mobile-job-orders/${encodeURIComponent(jo_number)}/pdf`} download>
              <Button variant="outline"><Download size={16} className="mr-1" /> Download</Button>
            </a>
            {!isSales && (
              <Button
                data-testid="generate-payment-link-btn"
                onClick={() => setShowPL(true)}
                className="bg-orange-500 hover:bg-orange-600 text-white"
              >
                <LinkIcon size={16} className="mr-1" /> Generate Payment Link
              </Button>
            )}
          </div>
        </div>

        {/* Header info */}
        <Card className="border-zinc-300 rounded-sm">
          <CardContent className="p-6 grid grid-cols-2 md:grid-cols-4 gap-4">
            <Field label="OB Date" value={formatAnyDate(data.jo_date)} />
            <Field label="Quotation No" value={data.quotation_no} />
            <Field label="Quotation Date" value={formatAnyDate(data.quotation_date)} />
            <Field label="Status" value={<Badge variant="outline" className="uppercase">{data.status}</Badge>} />
            <Field label="Executive" value={data.executive_name} />
            <Field label="Approved By" value={data.approved_by} />
            <Field label="Delivery By" value={formatAnyDate(data.delivery_date)} />
            <Field label="Work Location" value={data.work_location} className="col-span-2 md:col-span-4" />
          </CardContent>
        </Card>

        {/* Overlay info */}
        {(overlay.is_urgent || overlay.on_hold) && (
          <Card className="border-amber-300 bg-amber-50 rounded-sm">
            <CardContent className="p-4 text-sm">
              {overlay.on_hold && <div><b>On Hold</b> · {overlay.hold_reason || ''}</div>}
              {overlay.attribution && <div className="text-xs text-zinc-600 mt-1">By {overlay.attribution.by || overlay.attribution.user || '—'}</div>}
            </CardContent>
          </Card>
        )}

        {/* Items */}
        <Card className="border-zinc-300 rounded-sm">
          <CardHeader className="border-b border-zinc-300"><CardTitle className="text-lg">Items ({items.length})</CardTitle></CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-zinc-50 border-b border-zinc-300">
                <tr>
                  {['#', 'W', 'H', 'Category', 'Item', 'Profile', 'Shutters', 'Room', 'Opening', 'Qty', 'Remarks', 'SQFT', 'Amount'].map(h => (
                    <th key={h} className="text-left p-2 font-bold uppercase text-[10px] text-zinc-700">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr><td colSpan={13} className="p-6 text-center text-zinc-400">No items</td></tr>
                ) : items.map((it, i) => (
                  <tr key={i} className="border-b border-zinc-200">
                    <td className="p-2">{it.sl_no || i + 1}</td>
                    <td className="p-2">{it.width}</td>
                    <td className="p-2">{it.height}</td>
                    <td className="p-2">{it.category}</td>
                    <td className="p-2">{it.item || it.item_name}</td>
                    <td className="p-2">{it.profile || it.profile_type}</td>
                    <td className="p-2">{it.no_of_shutters}</td>
                    <td className="p-2">{it.room_type}</td>
                    <td className="p-2">{it.opening}</td>
                    <td className="p-2">{it.quantity}</td>
                    <td className="p-2">{it.remarks}</td>
                    <td className="p-2">{Number(it.sqft || 0).toFixed(2)}</td>
                    <td className="p-2 font-bold">{fmtINR(it.amount || 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>

        {/* Supplementaries */}
        {data.supplementaries && data.supplementaries.length > 0 && (
          <Card className="border-blue-300 rounded-sm">
            <CardHeader className="border-b border-blue-300"><CardTitle className="text-lg">Supplementaries ({data.supplementaries.length})</CardTitle></CardHeader>
            <CardContent className="p-4 space-y-2">
              {data.supplementaries.map((s, i) => (
                <div key={i} className="text-sm border-b border-zinc-200 pb-2 last:border-0">
                  <Link to={`/admin/mobile-job-orders/${s.jo_number}`} className="font-mono text-blue-600 hover:underline">{s.jo_number}</Link>
                  <span className="text-xs text-zinc-500 ml-3">{s.created_at}</span>
                </div>
              ))}
            </CardContent>
          </Card>
        )}
      </div>

      <GeneratePaymentLinkDialog
        open={showPL}
        onOpenChange={setShowPL}
        prefill={{
          customer_name: data.customer_name,
          customer_mobile: data.customer_mobile,
          // Amount is entered manually — Job Orders no longer carry an
          // order value (booked amount lives on the linked quotation).
          amount: undefined,
          jo_number: data.jo_number,
          quotation_number: data.quotation_no,
          description: `Payment for JO ${data.jo_number}`,
        }}
      />
    </Layout>
  );
}

function Field({ label, value, className = '' }) {
  return (
    <div className={className}>
      <Label className="text-xs uppercase text-zinc-500">{label}</Label>
      <div className="text-zinc-950 text-sm mt-0.5">{value || '—'}</div>
    </div>
  );
}

export default MobileJobOrderDetail;
export { MobileJobOrderDetail };
